import { Alert, Col, Row, Statistic, Typography } from 'antd';
import type { InboxItem } from './types';
import {
    marathonStopReasonLabel,
    notificationKindLabel,
    parseNotificationBody,
    type ParsedErrorLine,
} from './parseNotificationBody';

type Props = {
    item: InboxItem;
};

function errorSeverity(count: number, totalFailed: number): 'error' | 'warning' | 'default' {
    if (totalFailed <= 0) return 'default';
    const share = count / totalFailed;
    if (share >= 0.5) return 'error';
    if (share >= 0.2) return 'warning';
    return 'default';
}

function ErrorRow({ row, totalFailed }: { row: ParsedErrorLine; totalFailed: number }) {
    const sev = errorSeverity(row.count, totalFailed);
    return (
        <div
            style={{
                padding: '8px 10px',
                marginBottom: 8,
                borderRadius: 6,
                background:
                    sev === 'error'
                        ? 'rgba(255, 77, 79, 0.08)'
                        : sev === 'warning'
                          ? 'rgba(250, 173, 20, 0.08)'
                          : 'rgba(0,0,0,0.02)',
                border: '1px solid rgba(0,0,0,0.06)',
            }}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
                <Typography.Text strong>#{row.rank}</Typography.Text>
                <Typography.Text type="secondary">
                    {row.count} accounts
                    {row.pct != null ? ` · ${row.pct}% of failures` : ''}
                </Typography.Text>
            </div>
            <Typography.Text style={{ fontSize: 12 }}>{row.message}</Typography.Text>
        </div>
    );
}

export function NotificationDetailPanel({ item }: Props) {
    const parsed = parseNotificationBody(item.body, item.title);
    const kindLabel = notificationKindLabel(item, parsed);
    const failedMetric = parsed.metrics.find((m) =>
        String(m.label).toLowerCase().includes('failed'),
    );
    const totalFailed =
        typeof failedMetric?.value === 'number' ? failedMetric.value : Number(failedMetric?.value) || 0;

    const statMetrics = parsed.metrics.filter(
        (m) => typeof m.value === 'number' && !String(m.label).toLowerCase().includes('api'),
    );
    const apiCalls = parsed.metrics.find((m) => String(m.label).toLowerCase().includes('api'));
    const stop = parsed.kind === 'marathon' ? marathonStopReasonLabel(parsed.stopReason) : null;

    return (
        <div>
            <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
                {kindLabel}
                {' · '}
                {new Date(item.created_at).toLocaleString()}
            </Typography.Paragraph>

            {stop && (
                <Alert
                    type={stop.severity}
                    showIcon
                    message={stop.title}
                    description={(
                        <>
                            <div>{stop.detail}</div>
                            {parsed.checkpoint && (
                                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                    Checkpoint: {parsed.checkpoint}
                                    {parsed.stopReason && !/^[-–—]$/i.test(parsed.stopReason.trim())
                                        ? ` · code: ${parsed.stopReason}`
                                        : ''}
                                </Typography.Text>
                            )}
                        </>
                    )}
                    style={{ marginBottom: 16 }}
                />
            )}

            {parsed.topErrors.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                    <Typography.Text strong style={{ fontSize: 14, display: 'block', marginBottom: 12 }}>
                        Top failure errors this run
                    </Typography.Text>
                    {parsed.topErrors.map((row) => (
                        <ErrorRow key={row.rank} row={row} totalFailed={totalFailed} />
                    ))}
                </div>
            )}

            {parsed.kind === 'marathon' && parsed.topErrorsNone && parsed.topErrors.length === 0 && (
                <Alert
                    type={totalFailed > 0 ? 'warning' : 'success'}
                    showIcon
                    message={totalFailed > 0 ? 'Failures without error messages' : 'No failure errors in window'}
                    description={
                        totalFailed > 0
                            ? `${totalFailed} creator(s) failed in this window, but no error text was stored on those rows.`
                            : 'Top errors: none — no failed fetches with messages in this time window.'
                    }
                    style={{ marginBottom: 16 }}
                />
            )}

            {parsed.kind === 'marathon' &&
                !parsed.topErrorsNone &&
                parsed.topErrors.length === 0 &&
                totalFailed > 0 && (
                    <Alert
                        type="warning"
                        showIcon
                        message="Failures recorded"
                        description={`${totalFailed} failed in this window; detailed error breakdown was not included in this notification.`}
                        style={{ marginBottom: 16 }}
                    />
                )}

            {parsed.window && (
                <Alert
                    type="info"
                    showIcon={false}
                    message="Time window"
                    description={parsed.window}
                    style={{ marginBottom: 16, fontSize: 12 }}
                />
            )}

            {statMetrics.length > 0 && (
                <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                    {statMetrics.map((m) => (
                        <Col xs={12} sm={8} md={6} key={m.label}>
                            <Statistic
                                title={<span style={{ fontSize: 12 }}>{m.label}</span>}
                                value={m.value}
                                valueStyle={{
                                    fontSize: 20,
                                    color:
                                        m.highlight === 'success'
                                            ? '#389e0d'
                                            : m.highlight === 'error'
                                              ? '#cf1322'
                                              : m.highlight === 'warning'
                                                ? '#d48806'
                                                : undefined,
                                }}
                            />
                        </Col>
                    ))}
                </Row>
            )}

            {apiCalls && (
                <Typography.Text type="secondary" style={{ fontSize: 13, display: 'block', marginBottom: 16 }}>
                    {apiCalls.label}: {apiCalls.value}
                </Typography.Text>
            )}

            {(parsed.kind === 'ddex' ||
                parsed.kind === 'generic' ||
                (parsed.kind === 'marathon' && parsed.metrics.length === 0)) && (
                <Typography.Paragraph style={{ whiteSpace: 'pre-wrap', fontSize: 14, marginBottom: 0 }}>
                    {item.body}
                </Typography.Paragraph>
            )}
        </div>
    );
}
